import * as Sentry from "@sentry/react"
import React, { useContext } from "react"
import { webInterface } from "../../server/routers/_buildWebInterface"
import {
  ISlimStockArticleCodeMaster,
  ISlimStockArticleFilter,
  ISlimStockConfirmedDemand,
  ISlimStockHistoricalPO,
  ISlimStockLogistics,
  ISlimStockPurchaseOrder,
  ISlimStockStock,
} from "../Pages/Stock/SlimStock/slimStockSchema"
import { apiHost } from "../constants/apiHost"
import { joinUrl } from "../utils/utils"
import {
  ITrackingDTO,
  IWarehouseTransferDTO,
  IWarehouseTransferRequestDTO,
} from "../types/useExportInternalOrder"
import { extractMessageFromError } from "../../server/utils/extractMessageFromError"
import type { DashboardData } from "../../shared/dashboardData"
import type { IMailchimpSyncUpStatus } from "../../shared/mailchimpSyncReport"
import { String } from "aws-sdk/clients/apigateway"
import type { ISearchIndexRun } from "../../server/search/indexRuns"
import type { IProductStock } from "../../server/productLocations/aggregateProductStock"

export interface IWebsiteCustomer {
  id: string
  eosId: string
  user: { id: string; email: string }
  branch: { id: string; name: string }
}

interface IBarScanItem {
  eosId: string
  item: string
  partShip: boolean
  noPONoInv: boolean
  holdPick: boolean
  spare1: boolean
  spare2: boolean
  spare3: boolean
}

export type ISystemTask =
  | "dpdStatuses"
  | "createPendingStockImports"
  | "createPendingImports"
  | "cacheSupplierStatistics"
  | "generateProductMastersFromProducts"
  | "seedCategoriesFromCsv"
  | "productMasterHikVisionFullImport"
  | "esdProductImport"
  | "generateSalesRepsFromCustomerAnalysisCodes"
  | "associateInvoiceItemsToSalesReps"
  | "archiveExportedOrders"
  | "generateProductGroupMastersFromProductGroups"
  | "generateProductMasterAliases"
  | "buildCaches"
  | "buildImageCaches"

export interface IFedexTrackingDetail {
  receivedBy: {
    name: string
    type: string
    description: string
  } | null
  events: {
    description: string
    date: string
  }[]
}

export interface IDpdTrackingDetail {
  trackingnumber: string[] //"1355390751"
  parcelnumber: string[] //"1355390751"
  consignmentnumber: string[] //"1355390750"
  deliverypostcode: string[] //"CV11"
  deliverycountrycode: string[] //"GB"
  signedfordate: string[] //"2021-11-17"
  parcelcode: string[] //"1355390751*15709"
  trackingevents: {
    trackingevent: {
      date: string[] //"2021-11-17T09:30:00Z"
      code: string[] //"1"
      type: string[] //"DELIVERED"
      description: string[] //"Delivered, received by POD NAME"
      locality: string[] //"Leicester"
      countrycode: string[] //"GB"
      imagekey: string[] //""
    }[]
  }[]
}

export interface IFedexTrackingDetail {
  receivedBy: { name: string; type: string; description: string } | null
  events: {
    description: string
    date: string
  }[]
}

export interface I365Recipient {
  name: string
  address: string
}

export interface I365Email {
  "@odata.etag": string
  id: string
  subject: string
  sender: { emailAddress: I365Recipient }
  toRecipients: { emailAddress: I365Recipient }[]
  ccRecipients: { emailAddress: I365Recipient }[]
  receivedDateTime: string
  sentDateTime: string
  isRead: boolean
  isDraft: boolean
  isReadReceiptRequested: boolean
  hasAttachments?: boolean
  uniqueBody: { contentType: string; content: string }
}

export interface I365EmailAttachment {
  "@odata.context": string
  value: {
    "@odata.type": string
    "@odata.mediaContentType": string // "image/jpeg",
    // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    id: string
    lastModifiedDateTime: string
    name: string
    contentType: string // "image/jpeg", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    size: number
    isInline: boolean
    contentId: string
    contentLocation: null
    contentBytes: string
  }[]
}

export interface I365Attendee {
  type: string
  status: {
    response: string
    time: string
  }
  emailAddress: I365Recipient
}

export interface I365Event {
  "@odata.etag": string
  id: string
  createdDateTime: string
  subject: string
  isOnlineMeeting: boolean
  body: {
    contentType: string
    content: string
  }
  start: {
    dateTime: string
    timeZone: "UTC"
  }
  end: {
    dateTime: string
    timeZone: "UTC"
  }
  location: {
    displayName: string
    locationType: string
    uniqueIdType: string
    address: Record<string, string>
    coordinates: Record<string, string>
  }
  locations: {
    displayName: string
    locationType: string
    uniqueIdType: string
    address: Record<string, string>
    coordinates: Record<string, string>
  }[]
  attendees: I365Attendee[]
  organizer: {
    emailAddress: I365Recipient
  }
  onlineMeeting?: { joinUrl?: string }
  recurrence: {
    pattern: {
      type: string
      interval: number
      month: number
      dayOfMonth: number
      daysOfWeek: string[]
      firstDayOfWeek: string
      index: string
    }
    range: {
      type: string
      startDate: string
      endDate: string
      recurrenceTimeZone: string
      numberOfOccurrences: number
    }
  }
}

export interface I365EventInstance {
  "@odata.etag": string
  id: string
  createdDateTime: string
  lastModifiedDateTime: string
  changeKey: string
  categories: []
  transactionId: string
  originalStartTimeZone: string
  originalEndTimeZone: string
  iCalUId: string
  reminderMinutesBeforeStart: number
  isReminderOn: boolean
  hasAttachments: boolean
  bodyPreview: string
  subject: string
  importance: string
  sensitivity: string
  isAllDay: false
  isCancelled: false
  isOrganizer: true
  responseRequested: true
  seriesMasterId: string
  showAs: string
  type: string
  webLink: string
  onlineMeetingUrl: null
  isOnlineMeeting: true
  onlineMeetingProvider: string
  allowNewTimeProposals: boolean
  occurrenceId: string
  isDraft: boolean
  hideAttendees: boolean
  responseStatus: any
  body: any
  start: {
    dateTime: string
    timeZone: "UTC"
  }
  end: {
    dateTime: string
    timeZone: "UTC"
  }
  location: any
  locations: []
  recurrence: null
  attendees: any
  organizer: any
  onlineMeeting: any
}

export enum IHikVisionResponseCodes {
  "success" = "000000",
  "error" = "000001",
  "invalid_parameter" = "000002",
  "no_product_data" = "100001",
}

export type IHikVisionProductListData = { sapCode: string; productNumber: string }[]

export interface IHikVisionProductResponse<DataType> {
  code: IHikVisionResponseCodes
  message: "Success"
  data: DataType
}

export interface IHikVisionProductData {
  name: string
  productModel: string
  productPictures: string[]
  docs: {
    categoryName: string
    urls: string[]
  }[]
  functionFeatures: string[]
  specParameter: [
    {
      name: "Parameters"
      children: [
        { specItemName: "Description"; specItemValue: string },
        { specItemName: "Output Voltage"; specItemValue: string },
        { specItemName: "Output Current"; specItemValue: string },
        { specItemName: "Mount Option"; specItemValue: string },
        { specItemName: "Input Interface"; specItemValue: string },
        { specItemName: "Output Interface"; specItemValue: string },
      ]
    },
  ]
}

type ISlimStockIndex<T> = {
  data: T[]
  counts: Record<string, number>
  pagination?: {
    page: number
    size: number
    total: number
    totalPages: number
  }
}

export interface IEmail {
  to: {
    name: string
    email: string
  }[]
  fromEmail: string
  fromName: string
  subject: string
  html: string
  plain: string
  attachments: {
    name: string
    type: string
    data: string
  }[]
  wrapperProps: {
    company: "ssp" | "fortus"
  }
}

export type IStockData = {
  [productId: string]: {
    id: string
    stockCode: string
    totalInStock: number
    productLocations: {
      id: string
      warehouseId: string
      name: string
      freeStock: number
    }[]
  }
}

export type IProductMasterSearchResult = {
  hits: {
    code: string
    name: string
    description: string
    special_order_item?: boolean
    bom_product?: boolean
    brand: { id: string; name: string; fileboyLogoId: string }
    categories: { id: string; name: string }[]
    fbId: string | null
    objectID: string
    freeStock: { branchId: string; freeStock: number }[]
    totalNetworkStock: number
    totalUkStock: number
    totalIrelandStock: number
    operatingRegionUkStock?: number
    operatingRegionIrelandStock?: number
    isClearanceUk?: boolean
    isClearanceIreland?: boolean
    isEOL?: boolean

    _highlightResult: {
      code: {
        value: string
        matchLevel: string
        matchedWords: string[]
        fullyHighlighted: boolean
      }
      name: {
        value: string
        matchLevel: string
        matchedWords: string[]
        fullyHighlighted: boolean
      }
    }
  }[]
  facets: Record<string, Record<string, number>>
  nbHits: number
  page: number
  nbPages: number
  hitsPerPage: number
  query: string
  processingTimeMS: number
  serverTimeMS: number
}

export type IProductMasterAlgoliaRecord = {
  code: string
  alt: string
  name: string
  keywords: string
  description: string
  special_order_item?: boolean
  bom_product?: boolean
  brand: {
    id: string
    name: string
    featured: boolean
    fileboyLogoId: string
    disabled_on_fortus_live: boolean
  }
  division: { id: string; name: string }
  categories: { id: string; name: string }[]
  customerIds: string[] | ["none"]
  fbId: string | null
  freeStock: {
    branchId: string
    freeStock: number
  }[]
  totalNetworkStock: number
  totalUkStock: number
  totalIrelandStock: number
  operatingRegionUkStock?: number
  operatingRegionIrelandStock?: number
  isClearanceUk?: boolean
  isClearanceIreland?: boolean
  isEOL?: boolean
  productIds: string[]
  disabled_on_fortus_live: boolean
  country: "all" | "uk" | "ireland"
  objectID: string
} & { [key: `branchStock_${string}`]: number } & { [key: `tag.${string}`]: string[] }

export interface IWarehouseDespatchDTO {
  bsmWarehouseTransferID: number
  bsmWarehouseDespatchID: number
  warehouseIDFrom?: number
  warehouseIDTo?: number
  dateRequested?: Date | null
  requestedBy: string
  statusID: number
  createdDateTime: Date
  trackingNumber: string
  trackingMethod: string
  noOfParcels?: number
  weight?: number
  warehouseFromName: string
  description: string
  warehouseToName: string
  dateDespatched?: Date | null
  lines: IWarehouseDespatchLineDTO[]
}

export interface IWarehouseDespatchLineDTO {
  bsmWarehouseDespatchLineID: number
  bsmWarehouseTransferLineID: number
  bsmWarehouseDespatchID: number
  warehouseIDFrom?: number
  warehouseIDTo?: number
  itemID?: number
  code: string
  name: string
  quantity: number
  quantityDespatched: number
  quantityReceived: number
  quantityThisReceipt: number
}

export type IProductLocationStockResponseItem = {
  id: string
  stock_code: string
  inStock: number
  /** Portion of inStock booked in on a goods receipt but not yet confirmed by a Sage import */
  provisionalStock?: number
  allocatedStock: number
  explicitlyAllocatedStock: number
  onEosPurchaseOrderStock: number
  onSagePurchaseOrderStock: number
  onEosSalesOrderStock: number
  onSageSalesOrderStock: number
  avgMonthlySales: number
  name: string
  onSalesOrderStock: number
  onPurchaseOrderStock: number
  allocatedOrders?: {
    id: string
    order_number: string
    quantity: number
    allocatedQuantity: number
    isDelivery: boolean
  }[]
  onOrder?: {
    id?: string
    order_number: string
    quantity: number
    requested_delivery_date?: string
    created_at: string
    /** Which purchase orders this line came from (absent on entries cached before this was added) */
    source?: "eos" | "sage"
  }[]
}

interface IBinCreate {
  type: "create"
  name: string
  eosWarehouseId: string
}
interface IBinUpdate {
  type: "update"
  newBinName: string
  previousBinName: string
  eosProductLocationId: string
}
type IBaseBinMutation = {
  eosBranchId: string
  eosProductId: string
}
type IBinMutation = IBaseBinMutation & (IBinCreate | IBinUpdate)

export type ICacheReport =
  | "marginByCustomer"
  | "stockValue"
  | "averageMargin"
  | "marginThreshold"
  | "salesRepsMargin"
  | "stockCover"
  | "branchSummary"
  | "marginBySupplier"
  | "dailyCentralSales"
  | "dailyCentralSalesIreland"

const apiProvider = createApiProvider({
  getCache(request, key: string) {
    return request<any>(`cache/get-cache`, { key })
  },
  setCache(request, key: string, data: any, ttl: number) {
    return request<any>(`cache/set-cache`, { key, data, ttl })
  },
  /** `fresh: true` skips the 60s server cache; use it where a decision is made on the number. */
  productLocationsStock(
    request,
    productLocationIds: string[],
    opts?: { onlyValues?: boolean; fresh?: boolean },
  ) {
    return request<Record<string, IProductLocationStockResponseItem>>(
      `api/product-locations/stock`,
      {
        productLocationIds,
        opts,
      },
      {
        retries: 3,
      },
    )
  },
  /**
   * Stock totalled per product (the replacement for `fetchStockData({ productIds })`), keyed by product id.
   * `warehouseIds` limits which locations count; `allocationAttribute` forces the allocated column
   * (default: the product's branch setting); `fresh` skips the 60s server cache.
   */
  productsStock(
    request,
    productIds: string[],
    opts?: {
      warehouseIds?: string[]
      allocationAttribute?: "quantity" | "allocated_quantity"
      fresh?: boolean
    },
  ) {
    return request<Record<string, IProductStock>>(`api/product-locations/stock-by-product`, {
      productIds,
      opts,
    })
  },
  /**
   * Stock for individual product locations (the replacement for `fetchStockData({ productLocationIds })`),
   * keyed by location id. `warehouseIds`, `allocationAttribute` and `fresh` work as in `productsStock`; with no
   * `allocationAttribute` the quantity column is used, so pass `allocated_quantity` for a branch that uses
   * explicit allocations.
   */
  locationsStock(
    request,
    productLocationIds: string[],
    opts?: {
      warehouseIds?: string[]
      allocationAttribute?: "quantity" | "allocated_quantity"
      fresh?: boolean
    },
  ) {
    return request<Record<string, IProductStock>>(`api/product-locations/stock-by-location`, {
      productLocationIds,
      opts,
    })
  },
  /**
   * The ids of the branch's open orders that are ready to fulfil (every product line covered by stock now).
   * The orders list "Ready" filter on explicit-allocation branches uses it, as that figure is not a column.
   */
  readyOrderIds(request, branchId: string) {
    return request<{ ids: string[] } | { error: string }>(`api/product-locations/ready-orders`, {
      branchId,
    })
  },
  alterStock(
    request,
    data: {
      locationId: string
      stockChange: number
      binName?: string
    },
  ) {
    return request<{
      success: boolean
      previousValues: {
        free_stock: number
        total_stock: number
        allocated_stock: number
      }
      newValues: {
        free_stock: number
        total_stock: number
        allocated_stock: number
      }
      binUpdated: string
    }>(`api/product-locations/alter-stock`, data)
  },
  productLocationsStockClear(request, opts: { productLocationIds?: string[] }) {
    return request(`api/product-locations/stock-clear-cache`, opts)
  },
  consolidateOrderRequestsForBranch(request, branchId: string) {
    return request<{ success: true }>(`order-requests/consolidateOrderRequests/${branchId}`, {})
  },
  sendFulfillmentEmail(
    request,
    id: string,
    data: { name: string; email: string; subject: string; body: string },
  ) {
    return request<{ success: true } | { error: string }>(`fulfillments/${id}/email`, data)
  },
  createDpdShipment(request, deliveryId: string) {
    return request<{ success: true } | { error: string }>(
      `/deliveries/${deliveryId}/create_dpd_shipment`,
    )
  },
  createDpdIrelandShipment(request, deliveryId: string) {
    return request<{ saved: boolean } | { error: string }>(
      `/deliveries/${deliveryId}/create_dpd_ireland_shipment`,
    )
  },
  fetchDpdServices(request, deliveryId: string) {
    return request<
      { services: { id: string; product: string; service: string }[] } | { error: string }
    >(`/deliveries/${deliveryId}/dpd_services`)
  },
  barscanUpload(request, orderId: string) {
    return request<{ errors: [] } | { errors: []; message: string }>(`/barscan/upload/${orderId}`)
  },
  barscanGetItem(request, itemId: string) {
    return request<{ items: IBarScanItem[] } | { errors: []; message: string }>(
      `/barscan/item/${itemId}`,
    )
  },
  barscanIndex(request, branchId: string) {
    return request<{ items: IBarScanItem[] } | { errors: []; message: string }>(`/barscan/index`, {
      branchId,
    })
  },
  barscanInternalTransfer(request, orderId: string, data: IWarehouseTransferRequestDTO) {
    return request<IWarehouseTransferRequestDTO | { error: string }>(
      `/barscan/internal-transfer/export/${orderId}`,
      data,
    )
  },
  barscanInternalTransferV2(request, orderId: string, data: IWarehouseTransferRequestDTO) {
    return request<IWarehouseTransferRequestDTO | { error: string }>(
      `/barscan/internal-transfer/export-v2/${orderId}`,
      data,
    )
  },
  barscanInternalTransferGet(request, orderId: string) {
    return request<IWarehouseTransferDTO | { rawError: { error: boolean; message: string } }>(
      `/barscan/internal-transfer/get/${orderId}`,
    )
  },
  barscanInternalTransferTracking(request, orderId: string) {
    return request<ITrackingDTO | { error: string }>(
      `/barscan/internal-transfer/tracking/${orderId}`,
    )
  },
  barscanTransferTrackingDetails(request, orderId: string) {
    return request<ITrackingDTO | { error: string }>(`/barscan/tracking-details/${orderId}`)
  },
  barscanOrderWarehouseDespatches(request, orderId: string) {
    return request<IWarehouseDespatchDTO[] | { rawError: { error: boolean; message: string } }>(
      `/barscan/warehouse-despatches/${orderId}`,
    )
  },
  barscanInternalDespatchUpdate(
    request,
    orderId: string,
    data: { despatchLineID: number; quantityReceived: number }[],
  ) {
    return request<
      { result: string; despatchLineID: number; quantityReceived: number }[] | { error: string }
    >(`/barscan/internal-transfer/despatch-update/${orderId}`, data)
  },
  emailPreview(request, emailName: string, emailData: Record<string, any>) {
    return request<Record<string, { html: string; subject: string }>>(
      `/emails/preview/${emailName}`,
      emailData,
    )
  },
  emailCustomerSignUp(request, userId: string) {
    return request<{ ok: boolean }>(`/emails/customer-sign-up/${userId}`)
  },
  emailCustomerOrderReceived(request, jobId: string) {
    return request<{ ok: boolean }>(`/emails/order-received/${jobId}`)
  },
  emailCustomerOrderGenerated(request, orderId: string) {
    return request<{ ok: boolean }>(`/emails/order-generated/${orderId}`)
  },
  emailCustomerOrderDispatched(request, deliveryId: string) {
    return request<{ ok: boolean }>(`/emails/order-dispatched/${deliveryId}`)
  },
  emailBranchRmaCreated(request, id: string) {
    return request<{ ok: boolean; message?: string }>(`/emails/return-request/${id}`)
  },
  emailSend(
    request,
    data: {
      to: { name: string; email: string }
      subject: string
      body: string
    },
  ) {
    return request<Record<string, { html: string; subject: string }>>(`/emails/send`, data)
  },
  emailOrderAcknowledgement(request, orderId: string) {
    return request<Record<string, { html: string; subject: string }>>(
      `/emails/order-acknowledgement/${orderId}`,
      {},
    )
  },
  emailGoodsReceiptShortfall(
    request,
    orderId: string,
    items: { stock_code: string; body: string; quantity: number; received: number }[],
  ) {
    return request<Record<string, { html: string; subject: string }>>(
      `/emails/goods-receipt-shortfall/${orderId}`,
      { items },
    )
  },
  dpdTrackingStatus(request, id: string) {
    return request<IDpdTrackingDetail[]>(`/dpd/tracking/${id}`)
  },
  sendNotification(
    request,
    notificationData: {
      to: {
        emails?: string[]
        phoneNumbers?: string[]
        userIds?: string[]
      }
      content: {
        heading: string
        body?: string
        route?: string
        attachment?: { fileName: string; cacheBase64: string; cacheType: string }
      }
    },
  ) {
    return request<{ status: "OK"; sent: { emails: string[]; users: { id: string }[] } }>(
      "/notifications/send",
      notificationData,
    )
  },
  hikVisionProductList(request) {
    return request<IHikVisionProductResponse<IHikVisionProductListData>>(
      "/product-masters/hik-vision/product-list",
    )
  },
  hikVisionProduct(request, productNumber: string | number) {
    return request<IHikVisionProductResponse<IHikVisionProductData>>(
      `/product-masters/hik-vision/product/${productNumber}`,
    )
  },
  productMastersSeedCategoriesData(request) {
    return request<{ done: true }>(`/product-masters/seed-category-data`)
  },
  productMastersGenerateFromProducts(request) {
    return request<{ done: true }>(`/product-masters/generate-from-products`)
  },
  productGroupMastersGenerateFromProductGroups(request) {
    return request<{ done: true }>(`/product-group-masters/generate-from-groups`)
  },
  productMastersEsdImport(request) {
    return request<{ done: true }>(`/product-masters/esd-import`)
  },
  productMastersHikVisionFullImport(request) {
    return request<{ done: true }>(`/product-masters/hik-vision/full-import`)
  },
  send365Mail(
    request,
    args: {
      subject: string
      body: string
      userId: string
      attachment?: {
        type: "quote" | "orderAcknowledgement" | "purchaseOrder" | "priceList"
        id: string
        data?: Record<string, any>
      }
      additionalData?: {
        includeQuoteAttachments?: boolean
      } | null
      recipientEmailAddresses: string[]
    },
  ) {
    return request<{ success: false; error: any } | { success: true }>(`/microsoft/sendMail`, args)
  },
  microsoftEmailImport(
    request,
    args: {
      userId: string
      customerId: string
    },
  ) {
    return request<{ error?: boolean; message?: string; redirect?: "signout" | string }>(
      `/microsoft/import_mail`,
      args,
    )
  },
  get365MailShowAttachment(request, args: { emailId: string; userId: string }) {
    return request<
      | {
          success: boolean
          data: I365EmailAttachment
        }
      | ({ success: false } & Record<string, any>)
    >(`/microsoft/readMail/${args.emailId}/attachments`, args)
  },
  microsoftEventImport(
    request,
    args: {
      userId: string
      customerId: string
    },
  ) {
    return request<{ error?: boolean; message?: string; redirect?: "signout" | string }>(
      `/microsoft/import_events`,
      args,
    )
  },
  microsoftEventImportSalesRep(request, args: { salesRepId: string }) {
    return request<{ error?: boolean; message?: string; redirect?: "signout" | string }>(
      `/microsoft/import_events_sales_rep`,
      args,
    )
  },
  microsoftEmailImportSalesRep(request, args: { salesRepId: string }) {
    return request<{ error?: boolean; message?: string; redirect?: "signout" | string }>(
      `/microsoft/import_emails_sales_rep`,
      args,
    )
  },
  runServerTask(request, args: { task: ISystemTask }) {
    return request<{ success: false; error: any } | { success: true }>(`/tasks/run`, args)
  },
  generateReportCache(request, args: { report: ICacheReport; args?: any }) {
    return request<{ success: false; error: any } | { success: true }>(
      `/reports/generate-cache`,

      args,
    )
  },
  marginByProductCode(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeInternalCustomers?: boolean
      includeNonProductLines?: boolean
      showExcludedCustomers?: boolean
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              stock_code: string
              preferred_supplier: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              rebate: number
              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-product-code`, args)
  },
  marginByCategory(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeInternalCustomers?: boolean
      includeNonProductLines?: boolean
      showExcludedCustomers?: boolean
      salesRepId?: string
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              id: string
              name: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              rebate: number
              net_revenue: number
              net_margin: number

              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-category`, args)
  },
  marginBySalesRep(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeInternalCustomers?: boolean
      includeNonProductLines?: boolean
      showExcludedCustomers?: boolean
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              id: string
              name: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              rebate: number
              net_revenue: number
              net_margin: number

              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-sales-rep`, args)
  },
  marginByCustomer(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeInternalCustomers?: boolean
      includeNonProductLines?: boolean
      showExcludedCustomers?: boolean
      salesRepId?: string
      buyingGroupId?: string
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              id: string
              account_reference: string
              name: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              net_revenue: number
              net_margin: number
              rebate: number

              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-customer`, args)
  },
  marginBySupplier(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeNonProductLines?: boolean
      includeNoSales?: boolean
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              id: string
              account_reference: string
              name: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              net_revenue: number
              net_margin: number
              rebate: number

              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-supplier`, args)
  },
  marginByProductGroup(
    request,
    args: {
      branchId: string
      startDate: Date
      endDate: Date
      includeInternalCustomers?: boolean
      includeNonProductLines?: boolean
      showExcludedCustomers?: boolean
    },
  ) {
    return request<
      | {
          data: {
            rows: []
            error: any
          }
        }
      | {
          data: {
            rows: {
              name: string
              description: string
              quantity: number
              total_revenue: number
              total_cost: number
              margin: number
              margin_percentage: number
              rebate: number
              net_revenue: number
              net_margin: number
              realised_revenue: number
              realised_total_cost: number
              realised_margin: number
              realised_margin_percentage: number
              fully_realised: boolean
            }[]
            error: null
          }
        }
    >(`/reports/margin-by-product-group`, args)
  },
  searchProducts(request, args: { query: string; branchId: string }) {
    return request<{ success: true; ids: string[] } | { success: false; error: any }>(
      `/search/products`,
      args,
    )
  },
  searchProductsForSelect(
    request,
    args: {
      query: string
      branchId: string
      warehouseNames: string[]
      /** stock defaults to true, locations to false */
      include?: { stock?: boolean; locations?: boolean }
      /** unordered matches to return (default 50, at most 500) */
      limit?: number
    },
    opts?: { retries?: number },
  ) {
    return request<
      | {
          success: true
          data: {
            active: boolean
            available_stock: number
            body: string
            id: string
            is_consignment: string
            legacy_stock_code: string
            line_item_description: string
            stock_code: string
            sage_id: string
            /** only when include.locations is set */
            product_locations?: {
              id: string
              name: string
              specific_location: string
              warehouse?: { id: string; name: string }
            }[]
          }[]
        }
      | {
          success: false
          error: any
        }
    >(`/search/product-select`, args, opts)
  },
  searchProductMasters(
    request,
    args: {
      query: string
      branchId?: string
      customerId?: string
      hitsPerPage?: number
      page?: number
      inStockOnly?: boolean
      clearanceOnly?: boolean
      favouritesOnly?: boolean
      mainCategoryId?: string
      facets?: Record<string, string[] | undefined>
    },
  ) {
    return request<
      { success: true; result: IProductMasterSearchResult } | { success: false; error: any }
    >(`/search/product-masters`, args)
  },
  getProductMasterAlgoliaRecord(request, objectID: string) {
    return request<
      { success: true; record: IProductMasterAlgoliaRecord | null } | { success: false; error: any }
    >(`/search/product-master-by-id`, { objectID })
  },
  getProductMasterAlgoliaRecords(request, objectIDs: string[]) {
    return request<
      | { success: true; records: (IProductMasterAlgoliaRecord | null)[] }
      | { success: false; error: any }
    >(`/search/product-masters-by-ids`, { objectIDs })
  },
  indexProductMasters(request) {
    return request<{ success: true } | { success: false; error: any }>(
      `/search/index-product-masters`,
    )
  },
  startIndexProductMasters(request) {
    return request<{ success: true } | { success: false; error: any }>(
      `/search/index-product-masters/start`,
      {},
    )
  },
  getSearchIndexRuns(request) {
    return request<
      { success: true; runs: ISearchIndexRun[] } | { success: false; error: any }
    >(`/search/index-runs`, {})
  },
  indexProductMaster(request, productMasterId: string) {
    return request<
      | { success: true; action: "indexed" | "removed" | "not_found" }
      | { success: false; error: any }
    >(`/search/index-product-master`, { productMasterId })
  },
  slimStockLoadArticleCodeMaster(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockArticleCodeMaster>>(
      `/api/slimStock/loadArticleCodeMaster`,
      { branchId, page, size, query },
    )
  },
  slimStockLoadArticleFilterData(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockArticleFilter>>(
      `/api/slimStock/loadArticleFilterData`,
      {
        branchId,
        page,
        size,
        query,
      },
    )
  },
  slimStockLoadConfirmedDemandData(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockConfirmedDemand>>(
      `/api/slimStock/loadConfirmedDemandData`,
      { branchId, page, size, query },
    )
  },
  slimStockLoadHistoricalPurchaseOrderData(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockHistoricalPO>>(
      `/api/slimStock/loadHistoricalPurchaseOrderData`,
      { branchId, page, size, query },
    )
  },
  slimStockLoadLogisticsData(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockLogistics>>(`/api/slimStock/loadLogisticsData`, {
      branchId,
      page,
      size,
      query,
    })
  },
  slimStockLoadPurchaseOrderData(
    request,
    branchId: string,
    page?: number,
    size?: number,
    query?: string,
  ) {
    return request<ISlimStockIndex<ISlimStockPurchaseOrder>>(
      `/api/slimStock/loadPurchaseOrderData`,
      { branchId, page, size, query },
    )
  },
  slimStockLoadStockData(request, branchId: string, page?: number, size?: number, query?: string) {
    return request<ISlimStockIndex<ISlimStockStock>>(`/api/slimStock/loadStockData`, {
      branchId,
      page,
      size,
      query,
    })
  },
  xlsBranchStockValueReportEmail(request) {
    return request<any>("xls/branches/process-branch-stock-value-report")
  },
  emailCashSalesReportNow(request, branchId: string) {
    return request<{ success: boolean; sent: number }>(
      `xls/daily-cash-sales/email/branch`,
      { branchId },
      { throwError: true },
    )
  },
  generateBranchStockTakes(request, branchId: string) {
    return request<{ success: boolean }>(`/api/stock-takes/generate-auto`, { branchId })
  },
  mailchimpSyncUp(request) {
    return request<IMailchimpSyncUpStatus>(`/api/mailchimp/sync/up`, {}, { throwError: true })
  },
  mailchimpSyncUpStatus(request) {
    return request<IMailchimpSyncUpStatus>(
      `/api/mailchimp/sync/up/status`,
      {},
      { throwError: true },
    )
  },
  mailchimpSyncDown(request) {
    return request<any>(`/api/mailchimp/sync/down`, {})
  },
  mailchimpGetAllLists(request) {
    return request<any>(`/api/mailchimp/get_lists`, {})
  },
  mailchimpBuildWebhook(request) {
    return request<any>(`/api/mailchimp/sync/webhook`, {})
  },
  mailchimpListBatches(request) {
    return request<any>(`/api/mailchimp/batches`, {})
  },
  sendDailySalesReport(request, args?: { day?: "yesterday" }) {
    return request<any>(`xls/send-daily-sales`, args ?? {})
  },
  sendDailySalesReportTest(request, args?: { day?: "yesterday" }) {
    return request<any>(`xls/send-daily-sales-test`, args ?? {})
  },
  dailySalesXlsStatus(request, args: { includeInterCompany: boolean }) {
    return request<{
      generating: boolean
      generatedAt: string | null
      startedAt: string | null
      error: string | null
    }>(`xls/daily-sales/status`, args)
  },
  regenerateDailySalesXls(request, args: { includeInterCompany: boolean }) {
    return request<{ started: boolean }>(`xls/daily-sales/regenerate`, args)
  },
  previewDailySalesReport(request) {
    return request<{ preview: string }>(`xls/preview-daily-sales`, {})
  },
  sendDailySalesIrelandReport(request) {
    return request<any>(`xls/send-daily-sales/ireland`, {})
  },
  sendDailySalesIrelandReportTest(request) {
    return request<any>(`xls/send-daily-sales-test/ireland`, {})
  },
  previewDailySalesReportIreland(request) {
    return request<{ preview: string }>(`xls/preview-daily-sales/ireland`, {})
  },
  loginVerifySMSCode(request, data: { email: string; password: string; verificationCode: string }) {
    return request<{ success: true; authToken: string } | { success: false; message: string }>(
      "/auth/verify_sms_code",
      data,
    )
  },
  authLogin(request, args: { email: string; password: string }) {
    return request<
      | { success: false; message: string; methods?: string[] }
      | {
          success: true
          step: "fos-phone"
          additional: string
          message: string
          // No session token here either: /login withholds it until
          // /auth/verify_sms_code confirms the code it texted.
          userId: string
          methods?: IAuthMethods[]
        }
      | {
          success: true
          step: "login"
          message: string
          authToken: string
          userId: string
          methods?: IAuthMethods[]
        }
      | {
          success: true
          step: "verify_totp"
          message: string
          // No session token here: /auth/verify mints one once the code is accepted.
          userId: string
          methods?: IAuthMethods[]
        }
      | {
          success: true
          step: "set_up_totp"
          message: string
          qrCode: string
          secret: string
          userId: string
          methods?: IAuthMethods[]
        }
    >(`/auth/login`, args)
  },
  authValidate(request, args: { email: string; password: string }) {
    return request<
      | { success: false; message: string; methods?: IAuthMethods[] }
      | { success: true; type: string; methods?: IAuthMethods[] }
    >(`/auth/validate`, args)
  },
  authVerify(request, args: { token: string; email: string; password: string }) {
    return request<
      | { verified: false; error: string }
      | {
          verified: true
          authToken: string
        }
    >(`/auth/verify`, args)
  },
  generateUniversalPriceList(request) {
    return request<any>(`xls/universal-price-list/generate`)
  },
  rmaNotificationDetails(request, id, status?: "approved" | "rejected") {
    return request<{
      error?: string
      recipient:
        | {
            userIds: string[]
            emails?: undefined
          }
        | {
            emails: string[]
            userIds?: undefined
          }
      recipientReason: string
      subject: string
      body: string
      status: "approved" | "rejected"
    }>(`/rma/notify-details/${id}`, status ? { status } : undefined)
  },
  rmaNotificationOverride(
    request,
    id,
    data: {
      recipient: { userIds?: string[]; emails?: string[] }
      subject: string
      body: string
      status: "approved" | "rejected"
      senderId: string
    },
  ) {
    return request<{
      success: boolean
      error?: string
    }>(`/rma/notify-override/${id}`, data)
  },
  nextIdentifier(request, counter: "rma" | "supportTicket") {
    return request<{ identifier: string }>(`/rma/next-identifier/${counter}`, undefined, {
      throwError: true,
    })
  },
  rmaIrelandNotification(request, id) {
    return request<{ success: true }>(`/rma/notify/ireland/${id}`)
  },
  sageApiAuth(request) {
    return request<{ authorizationUri: string }>(`/sage-oauth/auth`, {}, { throwError: true })
  },
  sageOauthStatus(request) {
    return request<{ authenticated: boolean; expiresAt: string | null }>(
      `/sage-oauth/admin/status`,
      {},
      { throwError: true },
    )
  },
  sageOauthRefresh(request) {
    return request<{ success: true }>(`/sage-oauth/admin/refresh`, {}, { throwError: true })
  },
  sageOauthClear(request) {
    return request<{ success: true }>(`/sage-oauth/admin/clear`, {}, { throwError: true })
  },
  sageApiRefresh(request) {
    return request<{ success: true }>(`/sage-oauth/refresh`)
  },
  sageApiRequest(
    request,
    args: { method: string; endpoint: string; data?: any; branchId?: string },
  ) {
    return request<{ error: string } | { data: any }>(`/sage-200-api/request`, args)
  },
  richQuoteEmailPreview(request, quoteId: string) {
    return request<IEmail>(`/emails/rich-quote/${quoteId}/preview`)
  },
  richQuoteEmailSend(request, quoteId: string, heroContent) {
    return request<{ html: string; ok: boolean }>(`/emails/rich-quote/${quoteId}/send`, {
      content: heroContent,
    })
  },
  sageApiFetchAndFormatEOSOrderInfo(request, id: string) {
    return request<{
      order: any
      data: string
    }>(`/sage-200-api/export/fetchAndFormatEOSOrderInfo/${id}`)
  },
  sendInvoiceEmail(request, deliveryId: string) {
    return request<{ success: true }>(`/emails/invoice/${deliveryId}`)
  },
  sageApiExportBins(request, data: IBinMutation) {
    return request<{
      success: boolean
      error: string
    }>(`/sage-200-api/export/bins`, { mutation: data })
  },
  sageApiExportStockTransfer(request, id: string) {
    return request<{ success: boolean; error?: string }>(`/sage-200-api/export/stock-transfer`, {
      id: id,
    })
  },
  exportStockTransferViaSage(request, data: { ids: string[]; reference?: String }) {
    return request<{
      error?: string
      failedExports?: { transfer: { id: string }; error: string }[]
    }>(`/api/exportStockTransferViaSage`, data)
  },
  updateStockTransfersIfExportable(
    request,
    data: { updates: ({ id: string } & Record<string, unknown>)[]; deleteIds?: string[] },
  ) {
    // throwError so a refused edit (409) rejects, rather than resolving to { error }
    return request<{ success: boolean }>(`/api/updateStockTransfersIfExportable`, data, {
      throwError: true,
    })
  },
  exportInternalOrder(request, data: { id: string }) {
    return request<{
      success: boolean
      message: any
    }>(`/api/export-internal-order`, data, { throwError: true })
  },
  createBackOrder(
    request,
    data: {
      fromOrderId: string
      items: { id: string; quantityToMove: number }[]
      autoEmailOrderAcknowledgementOn?: string
    },
  ) {
    return request<{ backOrderId: string; id: string; identifier: string }>(
      `/api/create-back-order`,
      data,
      { throwError: true },
    )
  },
  validateOrderForConsolidation(request, data: { orderId: string }) {
    return request<{ canConsolidate: boolean; message: string }>(
      `/api/validate-order-for-consolidation`,
      data,
      { throwError: true },
    )
  },
  markOrderForConsolidation(request, data: { orderId: string }) {
    return request<{ success: true }>(`/api/mark-order-for-consolidation`, data, {
      throwError: true,
    })
  },
  nextJobIdentifierForBranch(request, branchId: string) {
    return request<{ jobIdentifier: string } | { error: string }>(
      `/jobs/next-identifier/${branchId}`,
    )
  },
  bugReport(
    request,
    data: {
      currentUrl: string
      urlHistory: string[]
      currentUser: any
      screenshot?: string
      message?: string
    },
  ) {
    return request<{ success: true }>(`/bug-report/send`, data)
  },
  exportProductPriceToSage(request, priceQueueItemId: string) {
    return request<{
      success: boolean
      error?: string
    }>(`/sage-product-export/export/price/${priceQueueItemId}`)
  },
  exportProductToSage(request, productId: string) {
    return request<{
      success: boolean
      error?: string
    }>(`/sage-product-export/export/${productId}`)
  },
  exportOrderToSage(request, orderId: string, options?: { despatch?: boolean }) {
    return request<{
      success: boolean
      error?: string
      // Present when the export was asked to despatch the order too. The export itself succeeded
      // whenever there is no top level `error`, so a despatch failure is reported in here rather
      // than as a failed export.
      despatch?: {
        success: boolean
        error?: string
        despatchedLines?: number
        alreadyDespatched?: boolean
      }
    }>(`/sage-order-export/export/${orderId}`, { despatch: options?.despatch ?? false })
  },
  despatchOrderInSage(request, orderId: string) {
    return request<{
      success: boolean
      error?: string
      despatchedLines?: number
      alreadyDespatched?: boolean
    }>(`/sage-order-export/despatch/${orderId}`)
  },
  auditSageOrder(request, orderId: string) {
    return request<{
      success: boolean
      error?: string
      data?: any
    }>(`/sage-order-export/audit-sage-order/${orderId}`)
  },
  exportPurchaseOrderToSage(
    request,
    purchaseOrderId: string,
    options?: { branchId?: string; sendEmail?: boolean },
  ) {
    return request<{
      success: boolean
      error?: string
      purchaseOrderId?: string
      data?: any
      message?: string
    }>(`/sage-order-export/export-purchase-order/${purchaseOrderId}`, {
      branchId: options?.branchId,
      sendEmail: options?.sendEmail ?? false,
    })
  },
  /** Why an exported purchase order can't be updated in Sage (null if it can), without calling Sage */
  getPurchaseOrderSageUpdateBlocker(request, purchaseOrderId: string) {
    return request<{ success: boolean; error?: string; blocker?: string | null }>(
      `/sage-order-export/update-purchase-order/${purchaseOrderId}/blocker`,
    )
  },
  /** What updating the purchase order in Sage would change. Nothing is sent to Sage. */
  previewPurchaseOrderSageUpdate(request, purchaseOrderId: string) {
    return request<{
      success: boolean
      error?: string
      changes?: {
        type: "header" | "line_changed" | "line_added" | "line_removed"
        description: string
      }[]
    }>(`/sage-order-export/update-purchase-order/${purchaseOrderId}/preview`, {})
  },
  updatePurchaseOrderInSage(request, purchaseOrderId: string, options?: { sendEmail?: boolean }) {
    return request<{
      success: boolean
      error?: string
      purchaseOrderId?: string
      updated?: boolean
      changes?: { type: string; description: string }[]
      emailError?: string
      message?: string
    }>(`/sage-order-export/update-purchase-order/${purchaseOrderId}`, {
      sendEmail: options?.sendEmail ?? false,
    })
  },
  exportCustomerToSage(request, customerId: string) {
    return request<{
      success: boolean
      error?: string
      customerId?: string
    }>(`/sage-customer-export/export/${customerId}`)
  },
  generateDashboardData(
    request,
    data: { dashboardType: "branch" | "salesRep" | "customer" | "subCustomer"; recordId: string },
  ) {
    return request<DashboardData>(`/api/generate-dashboard-data`, data, { throwError: true })
  },
  confirmPOReceived(
    request,
    data: {
      purchaseOrderId: string
      reference: string
      despatchLines: {
        itemId: string
        quantity: number
      }[]
    },
  ) {
    return request<unknown>(`/api/confirm-po-received`, data, { throwError: true })
  },
  checkMrgeResponse(request, data: { id: string }) {
    return request<{ success: boolean; error?: string }>(`/api/check-mrge-response`, data, {
      throwError: true,
    })
  },
  mrgeProcessStockTakeExport(request, data: { stockTakeExportId: string }) {
    return request(`/api/mrge-process-stock-take-export`, data, { throwError: true })
  },
  mrgeProcessSagePurchaseOrderReceipt(request, data: { sagePurchaseOrderReceiptId: string }) {
    return request(`/api/mrge-process-sage-purchase-order-receipt`, data, { throwError: true })
  },
  mrgeMarkPurchaseOrderAsReceived(
    request,
    data: {
      purchaseOrderId: string
      items: { itemId: string; quantity: number; warehouse: string; binName?: string }[]
    },
  ) {
    return request<{ success: boolean; error?: string }>(
      `/api/mrge-mark-purchase-order-as-received`,
      data,
      {
        throwError: true,
      },
    )
  },
  syncSagePurchaseOrderReceipts(request, data: { sagePurchaseOrderId: string }) {
    return request(`/api/sync-sage-purchase-order-receipts`, data, { throwError: true })
  },
  syncSagePurchaseOrderReceiptsBatch(request, data?: { sourceBranchId?: string }) {
    return request<{ synced: number; failed: number }>(
      `/api/sync-sage-purchase-order-receipts-batch`,
      data ?? {},
      { throwError: true },
    )
  },
  processGoodsReceiptPendingAllocations(request, data?: { id?: string }) {
    return request<{
      processed: number
      results: {
        id: string
        status: "completed" | "cancelled" | "failed" | "pending" | "skipped" | "error"
        message?: string
      }[]
    }>(`/api/process-goods-receipt-pending-allocations`, data ?? {}, { throwError: true })
  },
  applyProvisionalGoodsReceiptAllocations(
    request,
    data: {
      ids?: string[]
      goodsReceiptOrderId?: string
      sagePurchaseOrderId?: string
      sagePurchaseOrderReceiptId?: string
    },
  ) {
    return request<{
      applied: number
      results: {
        id: string
        status: "provisional" | "completed" | "failed" | "cancelled" | "skipped" | "error"
        message?: string
      }[]
    }>(`/api/apply-provisional-goods-receipt-allocations`, data, { throwError: true })
  },
  reconcileProvisionalGoodsReceiptAllocations(request, data?: { id?: string }) {
    return request<{
      processed: number
      results: {
        id: string
        status: "provisional" | "completed" | "failed" | "cancelled" | "skipped" | "error"
        message?: string
      }[]
    }>(`/api/reconcile-provisional-goods-receipt-allocations`, data ?? {}, { throwError: true })
  },
  priceImportSavePriceBandChanges(request, masterPriceImportId: string) {
    return request<{ success: boolean; error?: string; result: boolean | null }>(
      `/api/price-imports/save-price-band-changes/${masterPriceImportId}`,
      {},
    )
  },
  bookInOrder(request, orderId: string, orderItemIds?: string[]) {
    return request<{ success: boolean; error?: string }>(`/orders/book-in/${orderId}`, {
      itemIds: orderItemIds,
    })
  },
  processFileGenerationQueue(request) {
    return request<{ success: boolean; error?: string }>(`/file-generation-queue/process`, {})
  },
  ...webInterface,
})

type IAuthMethods = "blocked"

export type IApi = ReturnType<typeof apiProvider.use>
export type IApiResponse<EndPoint extends keyof IApi> = Awaited<ReturnType<IApi[EndPoint]>>

export function useApi(): IApi {
  return apiProvider.use()
}

export const ApiProvider = apiProvider.Provider
export const ApiContext = apiProvider.Context

type IApiRequest = <T>(
  path: string,
  data?: any,
  opts?: { throwError?: boolean; retries?: number },
) => Promise<T>
type IProviderOptions = { [key: string]: (request: IApiRequest, ...data: [...any[]]) => any }
type IBoundApiProviderOptions<T extends IProviderOptions> = {
  [key in keyof T]: T[key] extends (request: any, ...data: infer Data) => infer Ret
    ? (...data: Data) => Ret
    : never
}

function createApiProvider<T extends IProviderOptions>(routes: T) {
  const Context = React.createContext<IBoundApiProviderOptions<T> | null>(null)

  return {
    use() {
      const context = useContext(Context)

      if (!context) {
        throw new Error("must use the ApiProvider")
      }

      return context
    },
    Context,
    Provider({ children }: { children: React.ReactNode }) {
      const skeem = window.skeem

      async function boundApiRequest(
        path: string,
        data?: any,
        opts?: { throwError?: boolean; retries?: number },
      ) {
        const url = joinUrl(apiHost, path)
        const headers: any = {}
        let body

        headers["X-Skeem-Auth-Token"] = await skeem.getAuthToken()

        if (data instanceof FormData) {
          body = data
        } else {
          headers["Content-Type"] = "application/json"
          body = JSON.stringify(data)
        }

        if (opts?.throwError) {
          let response: Response
          let responseText: string

          try {
            response = await retryApiRequest(() => fetch(url, { method: "POST", headers, body }), {
              maxRetries: opts?.retries || 0,
              retryDelay: 1000,
              retryCondition: result => {
                if (result.error) {
                  return true
                }
                return false
              },
            })
            responseText = await response.text()
          } catch (error) {
            console.error("API Error", error)
            Sentry.captureException(error)
            const message = "Error connecting to the API server"
            throw new Error("There was a problem connecting to the API server: " + message)
          }
          const isContentType = (type: string) => {
            const contentType = response.headers.get("Content-Type")
            return contentType && contentType.toLowerCase().includes(type.toLowerCase())
          }

          if (!response.ok) {
            let message = "There was a problem connecting to the API server (not OK)"

            if (isContentType("application/json")) {
              try {
                const json = JSON.parse(responseText)
                message = extractMessageFromError(json)
              } catch (error) {
                console.error("Invalid JSON", error)
                message = "Invalid JSON response from the API server"
              }
            } else if (isContentType("text/html")) {
              // default express error page
              const messageRegex = /<pre>(.*)<br>/
              const match = responseText.match(messageRegex)
              if (match && match[1]) {
                message = match[1].replace(/<.*?>/g, "")
              }
            } else {
              console.error("Unknown Content-Type", response.headers.get("Content-Type"))
              console.error("API Error", responseText)
            }
            throw new Error(message)
          }

          if (isContentType("application/json")) {
            const json = JSON.parse(responseText)
            if (json.error) {
              const message = extractMessageFromError(json)
              throw new Error(message)
            }
            if ("data" in json) {
              return json.data
            }
            console.warn("API response did not match { data?: any, error?: any }", json)
            return json
          } else {
            console.warn(
              "API responded with non-JSON",
              response.headers.get("Content-Type"),
              responseText,
            )
            return
          }
        }

        try {
          const response = await retryApiRequest(
            () => fetch(url, { method: "POST", headers, body }),
            {
              maxRetries: opts?.retries || 0,
              retryDelay: 1000,
              retryCondition: result => {
                if (result.error) {
                  return true
                }
                return false
              },
            },
          )
          const responseText = await response.text()

          try {
            const json = JSON.parse(responseText)

            if (!response.ok) {
              let message = "There was a problem connecting to the API server (not OK)"

              if (typeof json === "object" && json !== null && "error" in json) {
                if (json.error === true && "message" in json) {
                  message = extractMessageFromError(json)
                } else {
                  message = extractMessageFromError(json)
                }
              }
              return { error: message, rawError: json }
            }

            return json
          } catch (error) {
            console.error(error)
            return { error: "There was a problem connecting to the API server (invalid JSON)" }
          }
        } catch (e) {
          console.error(e)
          Sentry.captureException(e)
          if (opts?.throwError) {
            throw e
          }
          return { error: "There was a problem connecting to the API server" }
        }
      }

      const value: any = {}

      for (const key in routes) {
        value[key] = (...args: any[]) => routes[key](boundApiRequest, ...args)
      }

      return <Context.Provider value={value}>{children}</Context.Provider>
    },
  }
}

interface RetryOptions {
  maxRetries: number
  retryDelay: number
  retryCondition?: (result: any) => boolean
}
/**
 * Retry wrapper for API requests
 *
 * @param apiCall - Function that makes the API request
 * @param options - Configuration for retry behavior
 * @returns The result of the API call
 * @throws The last error encountered if all retries fail
 */
export async function retryApiRequest<T>(
  apiCall: () => Promise<T>,
  options: RetryOptions,
): Promise<T> {
  let lastError: unknown
  let currentDelay = options.retryDelay

  const exponentialBackoff = true

  if (options.maxRetries === 0) {
    return apiCall()
  }

  for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
    try {
      const result = await apiCall()

      // If retry condition is specified and evaluates to true, throw to trigger retry
      if (options.retryCondition && options.retryCondition(result)) {
        throw new Error("Retry condition met")
      }

      return result
    } catch (error) {
      lastError = error

      if (attempt === options.maxRetries) {
        console.error(`All retry attempts failed`, error)
        break
      }

      console.warn(
        `Attempt ${attempt + 1}/${options.maxRetries + 1} failed, retrying in ${currentDelay}ms`,
      )

      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, currentDelay))

      // Increase delay if using exponential backoff
      if (exponentialBackoff) {
        currentDelay *= 2
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("API request failed after multiple retry attempts")
}
